import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, StatusPill } from "@/components/AdminTools";
import BasketBar from "@/components/BasketBar";
import {
  addToBasket,
  isInBasket,
  removeFromBasket,
  subscribeBasket,
} from "@/lib/programBasket";
import { Boxes, Layers, Tag, Plus, Check, PackagePlus } from "lucide-react";

// Build a basket entry from an admin-curated atomic service. These services
// don't have a vendor attached (clinic decides later in Program Builder).
function svcToBasketItem(s) {
  return {
    vendor_id: null,
    vendor_name: null,
    vendor_category: s.category,
    name: s.name,
    description: s.description || "",
    wholesale_cost: Number(s.wholesale_cost ?? 0),
    suggested_retail_min: s.suggested_retail_min ?? null,
    suggested_retail_max: s.suggested_retail_max ?? null,
  };
}

export default function PortalCatalog() {
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("packages");
  // Tick to force a re-render when basket changes so per-row "Added" pills
  // stay in sync without making each row a separate component.
  const [, forceTick] = useState(0);
  useEffect(() => subscribeBasket(() => forceTick((x) => x + 1)), []);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/packages")])
      .then(([s, p]) => {
        setServices(s.data || []);
        setPackages(p.data || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const svcMap = useMemo(
    () => Object.fromEntries(services.map((s) => [s.id, s])),
    [services],
  );
  const visiblePkgs = packages.filter((p) => p.status === "active");
  const visibleSvcs = services.filter((s) => s.status === "active");

  const toggleService = (s) => {
    const item = svcToBasketItem(s);
    if (isInBasket(item)) {
      removeFromBasket(item);
      toast.success(`Removed "${s.name}" from your pending program.`);
    } else {
      addToBasket(item);
      toast.success(`Added "${s.name}" to your pending program.`);
    }
  };

  const addBundle = (p) => {
    const constituent = (p.service_ids || [])
      .map((sid) => svcMap[sid])
      .filter(Boolean);
    if (constituent.length === 0) {
      toast.message("This program has no services attached.");
      return;
    }
    let added = 0;
    let already = 0;
    for (const s of constituent) {
      const item = svcToBasketItem(s);
      if (isInBasket(item)) {
        already += 1;
      } else {
        addToBasket(item);
        added += 1;
      }
    }
    if (added > 0) {
      toast.success(
        `Added ${added} service${added === 1 ? "" : "s"} from "${p.name}" to your pending program${
          already > 0 ? ` (${already} already in basket)` : ""
        }.`,
      );
    } else {
      toast.message(`All services from "${p.name}" are already in your basket.`);
    }
  };

  const isBundleInBasket = (p) => {
    const constituent = (p.service_ids || [])
      .map((sid) => svcMap[sid])
      .filter(Boolean);
    if (constituent.length === 0) return false;
    return constituent.every((s) => isInBasket(svcToBasketItem(s)));
  };

  return (
    <ToolPageShell
      eyebrow="Clinic · Catalog"
      title="Programs & services"
      intro="Pick services or whole bundles to assemble a Pending Program. Continue into Program Builder to assign vendors and submit for approval."
      actions={
        <div
          className="inline-flex rounded-full border border-slate-200 bg-white p-1"
          data-testid="portal-catalog-tabs"
        >
          <TabBtn
            id="packages"
            label="Programs"
            active={tab === "packages"}
            onClick={() => setTab("packages")}
          />
          <TabBtn
            id="services"
            label="Atomic services"
            active={tab === "services"}
            onClick={() => setTab("services")}
          />
        </div>
      }
    >
      {loading && <div className="text-sm text-slate-500">Loading catalog…</div>}

      {!loading && tab === "packages" && (
        <div
          className="grid sm:grid-cols-2 gap-4"
          data-testid="portal-catalog-packages"
        >
          {visiblePkgs.map((p) => {
            const allIn = isBundleInBasket(p);
            const count = (p.service_ids || []).length;
            return (
              <div
                key={p.id}
                data-testid={`portal-pkg-${p.id}`}
                className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600 capitalize">
                      {p.category}
                    </div>
                    <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">
                      {p.name}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">{p.description}</p>
                  </div>
                  <Layers className="w-5 h-5 text-slate-300" />
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                  <Tag className="w-3 h-3" /> {p.pricing_model} ·{" "}
                  {p.suggested_billing_cycle || "—"}
                </div>
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-1.5">
                    Includes ({count})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(p.service_ids || []).map((sid) => (
                      <span
                        key={sid}
                        className="text-[11px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5"
                      >
                        {svcMap[sid]?.name || sid}
                      </span>
                    ))}
                    {count === 0 && (
                      <span className="text-[11px] text-slate-400">
                        No services attached
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                  <StatusPill status={p.status} />
                  <button
                    type="button"
                    onClick={() => addBundle(p)}
                    disabled={count === 0 || allIn}
                    data-testid={`portal-pkg-add-bundle-${p.id}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 transition-colors ${
                      allIn
                        ? "bg-emerald-50 text-emerald-700 cursor-default"
                        : count === 0
                          ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                          : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                  >
                    {allIn ? (
                      <>
                        <Check className="w-3.5 h-3.5" /> All services added
                      </>
                    ) : (
                      <>
                        <PackagePlus className="w-3.5 h-3.5" /> Add bundle
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
          {visiblePkgs.length === 0 && (
            <div className="sm:col-span-2 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
              No active programs yet.
            </div>
          )}
        </div>
      )}

      {!loading && tab === "services" && (
        <div
          className="overflow-x-auto rounded-2xl border border-slate-200 bg-white"
          data-testid="portal-catalog-services"
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Kind · Category</th>
                <th className="text-left px-4 py-3">Negotiated rate</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleSvcs.map((s) => {
                const inB = isInBasket(svcToBasketItem(s));
                return (
                  <tr key={s.id} data-testid={`portal-svc-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
                        <Boxes className="w-3.5 h-3.5 text-slate-400" /> {s.name}
                      </div>
                      <div className="text-[11px] text-slate-500 line-clamp-1">
                        {s.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {s.kind} · <span className="capitalize">{s.category}</span>
                    </td>
                    <td
                      className="px-4 py-3 text-slate-900 font-semibold tabular"
                      title="Negotiated client bill rate (set by Nodera with the vendor)"
                    >
                      {s.wholesale_cost != null ? `$${s.wholesale_cost}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => toggleService(s)}
                        data-testid={`portal-svc-toggle-${s.id}`}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${
                          inB
                            ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                            : "bg-slate-900 text-white hover:bg-slate-800"
                        }`}
                      >
                        {inB ? (
                          <>
                            <Check className="w-3.5 h-3.5" /> Added
                          </>
                        ) : (
                          <>
                            <Plus className="w-3.5 h-3.5" /> Add
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visibleSvcs.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-slate-500 py-10">
                    No services available yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <BasketBar testIdPrefix="portal-catalog-basket" />
    </ToolPageShell>
  );
}

function TabBtn({ id, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`portal-catalog-tab-${id}`}
      onClick={onClick}
      className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}
